import React, { ReactElement } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { View, Text, StyleSheet, ScrollView } from 'react-native';
import { subtitleFontSize, screenMargin, smallestMargin, smallestMarginPixels, smallestFontSize } from '_utils/sizes';
import { useThemeContext } from '_utils/themeContext';
import { useGetProduct } from '../../../_api/useCategories';
import { useThemeStyle, getRelatedDateString, WW, Theme } from '../../../_utils';
import { HeimeText, SelectorChip, SecondaryButton, CacheImage, Icon } from '../../../Components';
import { UnixTimeCode } from '../../../types/Utility';

interface ProductBookingDetailsProps {
    productId: number;
    show: boolean;
    productName: string;
    start: UnixTimeCode;
    end: UnixTimeCode;
    quantity: number;
    onNavigateToReservations?: () => void;
    instances: { label: string }[];
}

const ProductBookingDetails = ({
    productId,
    show,
    productName,
    start,
    end,
    quantity,
    instances,
    onNavigateToReservations,
}: ProductBookingDetailsProps): ReactElement => {
    const themedStyle = useThemeStyle(styles);
    const { t } = useTranslation();
    const { data } = useGetProduct(productId);
    const { theme } = useThemeContext();

    if (!show) {
        return <></>;
    }

    const startMoment = moment.unix(start);
    const [startString, endString] = getRelatedDateString(startMoment.toDate(), moment.unix(end).toDate());

    const filteredInstances = instances.map((instance) => instance.label).filter(Boolean);

    return (
        <>
            <ScrollView bounces={false} style={{ flex: 1 }} contentContainerStyle={{ flexGrow: 1, gap: screenMargin }}>
                <HeimeText variant="subtitle" style={themedStyle.infoTxt}>
                    <Text style={themedStyle.emphasis}>{data?.name ?? productName}</Text>
                    {t('transaction:has_been_reservedPost')}
                </HeimeText>
                {data?.pictures?.length ? (
                    <View style={{ justifyContent: 'center', alignItems: 'center' }}>
                        <CacheImage
                            source={data?.pictures[0]}
                            style={{ width: WW * 0.5, height: WW * 0.5, borderRadius: screenMargin }}
                        />
                    </View>
                ) : null}
                {filteredInstances.length ? (
                    <HeimeText
                        variant="subtitle"
                        style={{ marginHorizontal: screenMargin, alignSelf: 'center', color: theme.darkGray }}
                    >
                        {t('transaction:assigned_instances')}
                        <HeimeText style={{ fontWeight: 'bold' }}>{` ${filteredInstances.join(', ')}`}</HeimeText>
                    </HeimeText>
                ) : null}

                <View style={themedStyle.chipContainer}>
                    <SelectorChip title={startString} variant="WhitePrimary" containerStyle={themedStyle.chip} />
                    <SelectorChip title={endString} variant="WhitePrimary" containerStyle={themedStyle.chip} />
                    {filteredInstances.length
                        ? filteredInstances.map((instance) => (
                              <SelectorChip
                                  key={instance}
                                  title={instance}
                                  variant="WhitePrimary"
                                  containerStyle={themedStyle.chip}
                              />
                          ))
                        : null}
                    {quantity > 1 ? (
                        <SelectorChip
                            title={t('serviceExpanded:quantity', { quantity })}
                            variant="WhitePrimary"
                            containerStyle={themedStyle.chip}
                        />
                    ) : null}
                    <SelectorChip
                        title={
                            startMoment.isBefore(moment())
                                ? t('transaction:started')
                                : t('transaction:timeUntilStart', {
                                      formattedDuration: moment
                                          .duration(Math.abs(startMoment.diff(moment())))
                                          .humanize(),
                                  })
                        }
                        variant="PrimaryWhite"
                        containerStyle={themedStyle.chip}
                    />
                </View>
                <View style={{ flex: 1 }} />
                {data?.locks?.length ? (
                    <View
                        style={{
                            flexDirection: 'row',
                            marginHorizontal: screenMargin,
                            padding: smallestMarginPixels * 1.5,
                            gap: smallestMargin,
                            borderWidth: 1,
                            borderColor: theme.secondaryLight,
                            borderRadius: smallestMargin,
                            marginBottom: screenMargin,
                        }}
                    >
                        <Icon name="info" color="main" />
                        <HeimeText style={{ flex: 1, fontSize: smallestFontSize }}>
                            {t('transaction:explainLocks')}
                        </HeimeText>
                    </View>
                ) : null}
            </ScrollView>
            {onNavigateToReservations ? (
                <SecondaryButton
                    style={themedStyle.backButton}
                    text={t('transaction:reservations')}
                    onPress={onNavigateToReservations}
                />
            ) : null}
        </>
    );
};

const styles = (theme: Theme) =>
    StyleSheet.create({
        explainer: {
            fontSize: subtitleFontSize,
            marginRight: screenMargin,
            marginLeft: screenMargin,
            textAlign: 'center',
        },
        emphasis: {
            fontWeight: 'bold',
            color: theme.black,
        },
        infoTxt: {
            fontSize: subtitleFontSize,
            color: theme.darkGray,
            textAlign: 'center',
            marginTop: smallestMarginPixels * 10,
        },
        chipContainer: {
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: WW * 0.02,
            marginHorizontal: screenMargin,
        },
        chip: {
            marginLeft: 4,
            marginRight: 4,
            marginTop: 4,
            marginBottom: 4,
        },
        backButton: {
            marginLeft: screenMargin,
            marginRight: screenMargin,
            marginTop: 'auto',
            marginBottom: screenMargin,
        },
    });

export default ProductBookingDetails;
